import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import { GroupMemberRole, GroupMemberRoleFilter } from '@ms/yammer-graphql';

import { EntityStatus, Group, GroupRoleMembers, User, YammerState } from '../../state/types';
import { createFindById } from '../findById';
import { getGroupMemberSearchResults } from '../groupMemberSearch/selectors';

export const groupMemberRoleFilterMap: Record<GroupMemberRole, GroupMemberRoleFilter> = {
  [GroupMemberRole.EXPERT]: GroupMemberRoleFilter.EXPERTS,
  [GroupMemberRole.LEADER]: GroupMemberRoleFilter.LEADERS,
};

export type GroupAdminMap = Record<
  User['id'],
  {
    readonly id: User['id'];
    readonly isAdmin: boolean;
  }
>;

export const findById = createFindById('groupRoleMembers');

type GetStatusForId = (state: YammerState, groupId: Group['id'], roleFilter: GroupMemberRoleFilter) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId, roleFilter) =>
  getStatus(state.groupRoleMembers, getGroupRoleMemberKey(groupId, roleFilter));

type GetGroupRoleMemberKey = (groupId: Group['id'], roleFilter: GroupMemberRoleFilter) => string;
export const getGroupRoleMemberKey: GetGroupRoleMemberKey = (groupId, roleFilter) => `${groupId}-${roleFilter}`;

type GetGroupRoleMembers = (
  state: YammerState,
  groupId: Group['id'],
  roleFilter: GroupMemberRoleFilter
) => GroupRoleMembers['roleMembers'];
export const getGroupRoleMembers: GetGroupRoleMembers = (state, groupId, roleFilter) =>
  findById(state, getGroupRoleMemberKey(groupId, roleFilter))?.roleMembers || [];

type GetFilteredGroupRoleMembers = (
  state: YammerState,
  groupId: Group['id'],
  roleFilter: GroupMemberRoleFilter
) => GroupAdminMap;
export const getFilteredGroupRoleMembers: GetFilteredGroupRoleMembers = (state, groupId, roleFilter) => {
  const searchMembers = getGroupMemberSearchResults(state, groupId);
  const groupRoleMembers = getGroupRoleMembers(state, groupId, roleFilter);

  return groupRoleMembers.reduce<GroupAdminMap>((acc, { nodeId, isAdmin }) => {
    if (searchMembers.length === 0 || searchMembers.some(({ id }) => id === nodeId)) {
      return {
        ...acc,
        [nodeId]: { id: nodeId, isAdmin },
      };
    }

    return acc;
  }, {});
};
type hasUserGroupRole = (
  state: YammerState,
  roleFilter: GroupMemberRoleFilter,
  groupId: Group['id'],
  userId: User['id']
) => boolean;
export const hasUserGroupRole: hasUserGroupRole = (state, roleFilter, groupId, userId) =>
  getGroupRoleMembers(state, groupId, roleFilter).some((member) => member.nodeId === userId);
